import React from 'react';
import * as Headless from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon, DocumentIcon } from '@heroicons/react/24/outline';

import { Input } from '@oysterjs/ui/input';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { ValidationError } from '@oysterjs/core/graphql';

import { FileInput } from '~/components/file';
import {
  createDefaultImageState,
  getHeaderFooterImageLayoutHint,
  getHeaderFooterSlotPaths,
  HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT,
  type HeaderFooterSlotKey,
  type HeaderFooterSlotPaths,
  type HeaderFooterSlotState,
  type HeaderFooterState
} from '~/components/library/lib/header-footer';

/**
 * Editor for a single header/footer slot — radio toggle between text and
 * image, file upload + sizing inputs in image mode, plain text input in
 * text mode. Validation errors come pre-filtered by slot field-path so we
 * only render the messages that target this slot.
 */
function HeaderFooterSlot(props: {
  label: string;
  slot: HeaderFooterSlotState;
  paths: HeaderFooterSlotPaths;
  validationErrors: ValidationError[];
  getValidationError: (errors: ValidationError[], path: string[]) => string | undefined;
  onTextChange: (text: string) => void;
  onModeChange: (mode: 'text' | 'image') => void;
  onImagePick: (file: { upload?: File; existing?: { id: string } } | null) => void;
  onImageSpacingChange: (patch: Partial<HeaderFooterSlotState['image']>) => void;
  onImageRemove: () => void;
}) {
  const { slot, label, paths, validationErrors, getValidationError } = props;
  const hasImage = !!(slot.image.file || slot.image.upload || slot.image.existing);

  return (
    <Field>
      <Label>{label}</Label>
      <RadioGroup
        className="!space-y-0 flex gap-6 mb-3 mt-2"
        value={slot.mode}
        onChange={(value: string) => props.onModeChange(value as 'text' | 'image')}
        aria-label={`${label} mode selection`}
      >
        <RadioField>
          <Radio color="sky" value="text" />
          <Label>Text</Label>
        </RadioField>
        <RadioField>
          <Radio color="sky" value="image" />
          <Label>Image</Label>
        </RadioField>
      </RadioGroup>
      {slot.mode === 'text' ? (
        <>
          <Input
            value={slot.text}
            onChange={(event) => props.onTextChange(event.currentTarget.value)}
            placeholder="e.g., Document Title"
            invalid={!!getValidationError(validationErrors, paths.text)}
          />
          {getValidationError(validationErrors, paths.text) && (
            <ErrorMessage>{getValidationError(validationErrors, paths.text)}</ErrorMessage>
          )}
        </>
      ) : (
        <>
          {hasImage ? (
            <div data-slot="control" className="flex flex-row gap-4 overflow-hidden">
              <div className="flex flex-row gap-2 min-w-0 overflow-hidden">
                <DocumentIcon className="size-10 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                <div className="flex flex-col min-w-0 overflow-hidden">
                  <div
                    className="text-sm text-neutral-700 dark:text-neutral-300 truncate"
                    title={
                      slot.image.file?.name ||
                      (slot.image.upload ? slot.image.upload.name : '') ||
                      (slot.image.existing ? slot.image.existing.id : 'Unknown file')
                    }
                  >
                    {slot.image.file?.name ||
                      (slot.image.upload ? slot.image.upload.name : '') ||
                      (slot.image.existing ? slot.image.existing.id : 'Unknown file')}
                  </div>
                  <button
                    type="button"
                    onClick={props.onImageRemove}
                    className="text-left text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer self-start"
                  >
                    Remove File
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <FileInput
              multiple={false}
              onChange={(change) => {
                const fileInput = change.files[0];
                if (!fileInput) {
                  props.onImagePick(null);
                  return;
                }
                if ('upload' in fileInput && fileInput.upload) {
                  props.onImagePick({ upload: fileInput.upload });
                } else if ('existing' in fileInput && fileInput.existing) {
                  props.onImagePick({ existing: fileInput.existing });
                }
              }}
              invalid={
                !!getValidationError(validationErrors, paths.imageUpload) ||
                !!getValidationError(validationErrors, paths.image)
              }
            />
          )}
          {getValidationError(validationErrors, paths.imageUpload) && (
            <ErrorMessage>{getValidationError(validationErrors, paths.imageUpload)}</ErrorMessage>
          )}
          {getValidationError(validationErrors, paths.image) &&
            !getValidationError(validationErrors, paths.imageUpload) && (
              <ErrorMessage>{getValidationError(validationErrors, paths.image)}</ErrorMessage>
            )}
          <div className="mt-3 flex gap-4">
            <div>
              <Label>Max height (pt)</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="1"
                step="any"
                value={slot.image.maxHeightPt}
                onChange={(event) =>
                  props.onImageSpacingChange({ maxHeightPt: event.currentTarget.value })
                }
                placeholder={HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT}
                className="w-20"
                invalid={!!getValidationError(validationErrors, paths.imageMaxHeight)}
              />
              {getValidationError(validationErrors, paths.imageMaxHeight) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, paths.imageMaxHeight)}
                </ErrorMessage>
              )}
            </div>
            <div>
              <Label>Edge gap (pt)</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={slot.image.paddingPt}
                onChange={(event) =>
                  props.onImageSpacingChange({ paddingPt: event.currentTarget.value })
                }
                placeholder="0"
                className="w-20"
              />
            </div>
            <div>
              <Label>Body gap (pt)</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={slot.image.innerGapPt}
                onChange={(event) =>
                  props.onImageSpacingChange({ innerGapPt: event.currentTarget.value })
                }
                placeholder="0"
                className="w-20"
              />
            </div>
          </div>
          {(() => {
            const hint = getHeaderFooterImageLayoutHint(slot);
            return hint ? (
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>
            ) : null;
          })()}
        </>
      )}
    </Field>
  );
}

/** Section header rendered for the two collapsible groups. */
function SectionHeader(props: { label: string; expanded: boolean; onToggle: () => void }) {
  return (
    <Headless.Button
      type="button"
      data-slot="label"
      onClick={props.onToggle}
      className="flex w-full items-center justify-between text-left"
    >
      <Label className="font-medium">{props.label}</Label>
      {props.expanded ? (
        <ChevronUpIcon className="ml-4 h-5 w-5 flex-shrink-0 text-neutral-500 dark:text-neutral-400" />
      ) : (
        <ChevronDownIcon className="ml-4 h-5 w-5 flex-shrink-0 text-neutral-500 dark:text-neutral-400" />
      )}
    </Headless.Button>
  );
}

const HEADER_SLOTS: { key: HeaderFooterSlotKey; label: string }[] = [
  { key: 'headerLeft', label: 'Left' },
  { key: 'headerCenter', label: 'Center' },
  { key: 'headerRight', label: 'Right' }
];

const FOOTER_SLOTS: { key: HeaderFooterSlotKey; label: string }[] = [
  { key: 'footerLeft', label: 'Left' },
  { key: 'footerCenter', label: 'Center' },
  { key: 'footerRight', label: 'Right' }
];

/**
 * Collapsible Headers + Footers section of the HTML editor. Renders the six
 * slots using `HeaderFooterSlot`, with validation errors filtered per-slot.
 */
export function HtmlEditorHeaderFooterPanel(props: {
  headerFooter: HeaderFooterState;
  validationErrors: ValidationError[];
  getValidationError: (errors: ValidationError[], path: string[]) => string | undefined;
  updateHeaderFooterSlot: (
    slotKey: HeaderFooterSlotKey,
    updater: (slot: HeaderFooterSlotState) => HeaderFooterSlotState
  ) => void;
  setHeaderFooterSlotMode: (slotKey: HeaderFooterSlotKey, mode: 'text' | 'image') => void;
  clearValidationErrorForField: (path: string[]) => void;
  clearValidationErrorsWithPrefix: (prefixes: string[][]) => void;
}) {
  const [headerExpanded, setHeaderExpanded] = React.useState(false);
  const [footerExpanded, setFooterExpanded] = React.useState(false);

  const renderSlot = (slotKey: HeaderFooterSlotKey, label: string) => {
    const paths = getHeaderFooterSlotPaths(slotKey);

    return (
      <HeaderFooterSlot
        key={slotKey}
        label={label}
        paths={paths}
        slot={props.headerFooter[slotKey]}
        validationErrors={props.validationErrors}
        getValidationError={props.getValidationError}
        onTextChange={(text) => {
          props.updateHeaderFooterSlot(slotKey, (prev) => ({ ...prev, text }));
          props.clearValidationErrorForField(paths.text);
        }}
        onModeChange={(mode) => props.setHeaderFooterSlotMode(slotKey, mode)}
        onImagePick={(file) => {
          if (file == null) {
            props.updateHeaderFooterSlot(slotKey, (prev) => ({
              ...prev,
              image: createDefaultImageState()
            }));
            props.clearValidationErrorsWithPrefix([paths.image, paths.imageUpload]);
            return;
          }
          if (file.upload) {
            props.updateHeaderFooterSlot(slotKey, (prev) => ({
              ...prev,
              image: {
                fileId: null,
                file: null,
                upload: file.upload!,
                existing: null,
                maxHeightPt: prev.image.maxHeightPt || HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT,
                paddingPt: prev.image.paddingPt ?? '',
                innerGapPt: prev.image.innerGapPt ?? ''
              }
            }));
            props.clearValidationErrorForField(paths.imageUpload);
          } else if (file.existing) {
            props.updateHeaderFooterSlot(slotKey, (prev) => ({
              ...prev,
              image: {
                fileId: file.existing!.id,
                file: null,
                upload: null,
                existing: file.existing!,
                maxHeightPt: prev.image.maxHeightPt || HEADER_FOOTER_DEFAULT_IMAGE_HEIGHT_PT,
                paddingPt: prev.image.paddingPt ?? '',
                innerGapPt: prev.image.innerGapPt ?? ''
              }
            }));
            props.clearValidationErrorsWithPrefix([paths.image, paths.imageUpload]);
          }
        }}
        onImageSpacingChange={(patch) => {
          props.updateHeaderFooterSlot(slotKey, (prev) => ({
            ...prev,
            image: { ...prev.image, ...patch }
          }));
          if ('maxHeightPt' in patch) {
            props.clearValidationErrorForField(paths.imageMaxHeight);
          }
        }}
        onImageRemove={() => {
          props.updateHeaderFooterSlot(slotKey, (prev) => ({
            ...prev,
            image: createDefaultImageState()
          }));
          props.clearValidationErrorsWithPrefix([paths.image, paths.imageUpload]);
        }}
      />
    );
  };

  return (
    <>
      <Field>
        <SectionHeader
          label="Headers"
          expanded={headerExpanded}
          onToggle={() => setHeaderExpanded((v) => !v)}
        />
        {headerExpanded && (
          <>
            <Description>
              Configure headers for your documents. Use placeholders like {'{page}'} for page
              numbers, {'{pages}'} for total pages, and {'{date}'} for the current date.
            </Description>
            <div className="mt-2 space-y-3">
              {HEADER_SLOTS.map((s) => renderSlot(s.key, s.label))}
            </div>
          </>
        )}
      </Field>
      <Field>
        <SectionHeader
          label="Footers"
          expanded={footerExpanded}
          onToggle={() => setFooterExpanded((v) => !v)}
        />
        {footerExpanded && (
          <>
            <Description>
              Configure footers for your documents. Use placeholders like {'{page}'} for page
              numbers, {'{pages}'} for total pages, and {'{date}'} for the current date.
            </Description>
            <div className="mt-2 space-y-3">
              {FOOTER_SLOTS.map((s) => renderSlot(s.key, s.label))}
            </div>
          </>
        )}
      </Field>
    </>
  );
}
